// src/pages/MyCalendar.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import DashboardHeader from "../components/DashboardHeader";
import PlannerPostModal from "../components/PlannerPostModal";
import { getToken } from "../lib/session";
import logo from '../assets/creatorai_collage_transparent.png';

export default function MyCalendar() {
  const [previewPost, setPreviewPost] = useState(null);
  const openPreview = (post) => setPreviewPost(post);

  const monthLabelRef = useRef(null);
  const gridRef = useRef(null);
  const [current, setCurrent] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });
  const realToday = useRef(new Date());
  const selected = useRef(new Set()); // multi-select
  const [selectedCount, setSelectedCount] = useState(0);

  // NEW: UI state for brands + filters (Promo-style)
  const [brands, setBrands] = useState([{ id: "all", name: "All brands" }]);
  const [activeBrand, setActiveBrand] = useState({
    id: "all",
    name: "All brands",
  });
  const [brandsOpen, setBrandsOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [postTypeFilter, setPostTypeFilter] = useState({
    ai: true,
    drafts: false,
    scheduled: false,
    published: false,
  });
  const [socialFilter, setSocialFilter] = useState({
    facebook: false,
    instagram: false,
    x: false,
    linkedin: false,
    tiktok: false,
    youtube: false,
  });

  // read brand name from onboarding (for dropdown label only)
  useEffect(() => {
    try {
      const raw = localStorage.getItem("onboarding.brand");
      if (raw) {
        const b = JSON.parse(raw);
        if (b.businessName) {
          const list = [
            { id: "all", name: "All brands" },
            { id: "primary", name: b.businessName },
          ];
          setBrands(list);
          setActiveBrand(list[0]);
          return;
        }
      }
    } catch {
      // ignore
    }
  }, []);

  // simple helpers
  const ymdOf = (dateLike) => {
    const d = new Date(dateLike);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const todayBase = new Date(
    realToday.current.getFullYear(),
    realToday.current.getMonth(),
    realToday.current.getDate()
  );

  // Draw the grid
  useEffect(() => {
    const grid = gridRef.current;
    const monthLabel = monthLabelRef.current;
    if (!grid || !monthLabel) return;

    grid.innerHTML = "";
    const year = current.getFullYear();
    const month = current.getMonth();
    const monthName = current.toLocaleString("default", { month: "long" });
    monthLabel.textContent = `${monthName} ${year}`;

    const first = new Date(year, month, 1);
    const startDay = (first.getDay() + 6) % 7; // Monday
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const prevDays = new Date(year, month, 0).getDate();
    const total = 42;

    for (let slot = 0; slot < total; slot++) {
      const cell = document.createElement("div");
      cell.className =
        "min-h-[120px] rounded-md px-3 pt-2 text-center relative bg-white";
      const head = document.createElement("div");
      head.className = "flex items-center justify-between text-sm";

      const dayIdx = slot - startDay + 1;
      let thisDate = null;

      if (slot < startDay) {
        head.innerHTML = `<span class="text-slate-300">${prevDays - (startDay - slot - 1)}</span>`;
        cell.classList.add("opacity-50");
      } else if (dayIdx > daysInMonth) {
        head.innerHTML = `<span class="">${dayIdx - daysInMonth}</span>`;
        thisDate = new Date(year, month + 1, dayIdx - daysInMonth);
      } else {
        head.innerHTML = `<span class="">${dayIdx}</span>`;
        thisDate = new Date(year, month, dayIdx);
      }

      cell.appendChild(head);

      // event wrap
      const wrap = document.createElement("div");
      wrap.className = "mt-2 flex flex-col gap-2";
      wrap.setAttribute("data-slot", "events");
      cell.appendChild(wrap);

      // IMPORTANT: give EVERY real date cell a data-ymd
      if (thisDate) {
        const ymd = ymdOf(thisDate);
        cell.setAttribute("data-ymd", ymd);

        // only show "+" for today and future dates
        if (thisDate >= todayBase) {
          const plus = document.createElement("button");
          plus.className =
            "absolute top-2 right-2 w-8 h-8 rounded-full bg-sky-50 text-sky-700 border border-sky-100 hover:bg-sky-100";
          plus.textContent = "+";

          plus.addEventListener("click", (e) => {
            e.stopPropagation();
            openQuickCreate(ymd, plus);
          });

          cell.appendChild(plus);
        }
      }

      grid.appendChild(cell);
    }
  }, [current]);

  // Load items
  useEffect(() => {
    (async () => {
      const grid = gridRef.current;
      if (!grid) return;

      const y = current.getFullYear();
      const m = current.getMonth() + 1;

      // map ymd -> container
      const slotMap = new Map();
      grid.querySelectorAll("[data-ymd]").forEach((cell) => {
        const ymd = cell.getAttribute("data-ymd");
        slotMap.set(ymd, cell.querySelector('[data-slot="events"]'));
      });
      // clear
      [...slotMap.values()].forEach((w) => (w.innerHTML = ""));

      let items = [];
      try {
        const r = await fetch(
          `${process.env.REACT_APP_API}/planner/calendar?year=${y}&month=${m}`,
          {
            headers: { Authorization: `Bearer ${getToken()}` },
          }
        );
        const j = await r.json();
        items = j.items || [];
      } catch {
        // ignore
      }

      // render each item
      items.forEach((it) => {
        const when = ymdOf(it.scheduledFor);
        const slot = slotMap.get(when);
        if (!slot) return;

        const card = document.createElement("div");
        card.className =
          "group relative rounded-lg overflow-hidden border border-slate-200 bg-white shadow-sm hover:shadow-md text-left";
        card.style.cursor = "pointer";

        card.innerHTML = `
  <div class="relative rounded-lg overflow-hidden group cursor-pointer transition">
    ${
      it.thumbUrl
        ? `<img src="${it.thumbUrl}" alt="${escapeHtml(
            it.title || "Post"
          )}" class="w-full h-24 object-cover group-hover:scale-105 transition-transform" />`
        : `<div class='h-24 bg-slate-100 flex items-center justify-center text-slate-400 text-xs'>No Image</div>`
    }
    <div class="absolute bottom-0 bg-black/50 text-white text-[12px] font-semibold truncate w-full p-1">
      ${escapeHtml(it.title || "Untitled Post")}
    </div>
  </div>
  <div class="text-[11px] text-slate-500 mt-1 px-1">${it.status || "draft"}</div>
  <input type="checkbox" class="absolute top-2 left-2 w-4 h-4 hidden group-hover:block">
`;

        // open modal
        card.addEventListener("click", (e) => {
          if (e.target.tagName.toLowerCase() === "input") return;
          openPreview(it); // pass the full post object
        });

        // multi-select
        const cb = card.querySelector('input[type="checkbox"]');
        cb.addEventListener("change", () => {
          if (cb.checked) selected.current.add(it._id);
          else selected.current.delete(it._id);
          setSelectedCount(selected.current.size);
        });

        slot.appendChild(card);
      });
    })();
  }, [current]);

  // Quick create like Promo: small prompt -> generate
  async function openQuickCreate(ymd, anchorEl) {
    const existing = document.getElementById("quick-create-pop");
    if (existing) existing.remove();

    const pop = document.createElement("div");
    pop.id = "quick-create-pop";
    pop.className =
      "absolute z-[50] mt-10 right-2 w-[360px] rounded-xl bg-white border border-slate-200 shadow-xl p-3";
    pop.innerHTML = `
      <div class="text-sm font-semibold mb-2">Generate a new AI content</div>
      <textarea rows="3" class="w-full border rounded-md p-2 text-sm" placeholder="Describe the content to generate"></textarea>
      <div class="flex justify-end gap-2 mt-2">
        <button data-cancel class="px-3 py-1 border rounded-md">Cancel</button>
        <button data-go class="px-3 py-1 rounded-md bg-sky-600 text-white">Generate</button>
      </div>
    `;
    anchorEl.closest("div").appendChild(pop);

    pop.querySelector("[data-cancel]").onclick = () => pop.remove();
    pop.querySelector("[data-go]").onclick = async () => {
      const val = pop.querySelector("textarea").value.trim();
      if (!val) return;
      pop.querySelector("[data-go]").disabled = true;

      await fetch(`${process.env.REACT_APP_API}/planner/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ source: "topic", value: val, date: ymd }),
      });

      pop.remove();
      // reload current month
      setCurrent((d) => new Date(d));
    };
  }

  // bulk delete
  async function deleteSelected() {
    if (!selected.current.size) return;
    const ids = Array.from(selected.current);
    await fetch(`${process.env.REACT_APP_API}/planner/bulk-delete`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({ ids }),
    });
    selected.current.clear();
    setSelectedCount(0);
    setCurrent((d) => new Date(d)); // reload
  }

  const escapeHtml = (s) =>
    String(s).replace(/[&<>"]/g, (c) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
      }[c])
    );

  return (
    <div className="min-h-screen bg-[#f7f7fb] text-slate-700">
      <DashboardHeader />

      {/* HERO – closer to Promo */}
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 pt-10 pb-12 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h1 className="text-4xl md:text-5xl font-extrabold leading-tight tracking-tight text-slate-900">
              <b className="text-blue-500">CreatorAI</b> generates branded 
              <br />
              content for every day of the
              <br />
              month
            </h1>
            <p className="mt-4 text-slate-500 max-w-xl">
              View, edit, and publish your AI-generated content directly from
              your calendar.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button className="inline-flex items-center px-5 py-2.5 rounded-full bg-black text-white text-sm font-semibold hover:bg-slate-900">
                Watch the video
              </button>
              <button className="inline-flex items-center px-5 py-2.5 rounded-full bg-white text-sm font-semibold border border-slate-200 shadow-sm hover:bg-slate-50">
                + Generate AI content
              </button>
            </div>
          </div>

          <div className="relative flex justify-center md:justify-end">
            <div className="w-[340px] h-[220px] relative">
              {/* Replace the previous gradient with your new image */}
              <img src={logo} alt="AI Generated Content Preview" className="w-full h-full object-cover rounded-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* CONTROLS + CALENDAR */}
      <main className="max-w-7xl mx-auto px-6 pb-24 pt-6">
        {/* Top controls row */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          {/* Left: Today + month switcher */}
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => {
                const d = new Date();
                d.setDate(1);
                setCurrent(d);
              }}
              className="px-4 py-2 rounded-full bg-slate-50 text-slate-700 border border-slate-100"
            >
              Today
            </button>

            <div className="flex items-center gap-3 bg-white px-4 py-2 rounded-full shadow border border-slate-100">
              <button
                onClick={() =>
                  setCurrent((prev) => {
                    const d = new Date(prev);
                    d.setMonth(d.getMonth() - 1);
                    return d;
                  })
                }
                className="p-2 rounded-full hover:bg-slate-50"
              >
                ‹
              </button>

              <div
                ref={monthLabelRef}
                className="font-semibold text-slate-800 min-w-[140px] text-center"
              >
                —
              </div>

              <button
                onClick={() =>
                  setCurrent((prev) => {
                    const d = new Date(prev);
                    d.setMonth(d.getMonth() + 1);
                    return d;
                  })
                }
                className="p-2 rounded-full hover:bg-slate-50"
              >
                ›
              </button>
            </div>
          </div>

          {/* Right: Learn / Brands / Filters / Month */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Learn */}
            <button className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-xs font-medium text-slate-700 border border-slate-200 shadow-sm">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-amber-400 text-[10px]">
                💡
              </span>
              Learn
            </button>

            {/* Brands dropdown */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setBrandsOpen((o) => !o)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-xs font-medium text-slate-700 border border-slate-200 shadow-sm"
              >
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-900 text-[11px] text-white">
                  ⋮⋮
                </span>
                {activeBrand?.name || "All brands"}
                <span className="text-[10px]">▾</span>
              </button>

              {brandsOpen && (
                <div className="absolute right-0 mt-2 w-56 rounded-2xl bg-white border border-slate-200 shadow-lg z-20">
                  <div className="py-2 text-xs">
                    {brands.map((b) => (
                      <button
                        key={b.id}
                        className={`w-full text-left px-4 py-2 hover:bg-slate-50 ${
                          activeBrand?.id === b.id
                            ? "font-semibold text-slate-900"
                            : "text-slate-600"
                        }`}
                        onClick={() => {
                          setActiveBrand(b);
                          setBrandsOpen(false);
                        }}
                      >
                        {b.name}
                      </button>
                    ))}
                    <div className="mt-1 pt-2 border-t border-slate-100 px-4 text-[11px] text-slate-500">
                      Manage brands
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Filters dropdown */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setFiltersOpen((o) => !o)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-xs font-medium text-slate-700 border border-slate-200 shadow-sm"
              >
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-sky-400 text-[11px]">
                  ☰
                </span>
                Filters
                <span className="text-[10px]">▾</span>
              </button>

              {filtersOpen && (
                <div className="absolute right-0 mt-2 w-72 rounded-2xl bg-white border border-slate-200 shadow-lg z-20 p-4">
                  <div className="grid grid-cols-2 gap-4 text-xs text-slate-700">
                    <div>
                      <div className="font-semibold mb-2">Post type</div>
                      <div className="space-y-1">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={postTypeFilter.ai}
                            onChange={(e) =>
                              setPostTypeFilter((p) => ({
                                ...p,
                                ai: e.target.checked,
                              }))
                            }
                          />
                          <span>Suggested by AI</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={postTypeFilter.drafts}
                            onChange={(e) =>
                              setPostTypeFilter((p) => ({
                                ...p,
                                drafts: e.target.checked,
                              }))
                            }
                          />
                          <span>Drafts</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={postTypeFilter.scheduled}
                            onChange={(e) =>
                              setPostTypeFilter((p) => ({
                                ...p,
                                scheduled: e.target.checked,
                              }))
                            }
                          />
                          <span>Scheduled</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={postTypeFilter.published}
                            onChange={(e) =>
                              setPostTypeFilter((p) => ({
                                ...p,
                                published: e.target.checked,
                              }))
                            }
                          />
                          <span>Published</span>
                        </label>
                      </div>
                    </div>
                    <div>
                      <div className="font-semibold mb-2">Social media</div>
                      <div className="space-y-1">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={socialFilter.facebook}
                            onChange={(e) =>
                              setSocialFilter((p) => ({
                                ...p,
                                facebook: e.target.checked,
                              }))
                            }
                          />
                          <span>Facebook</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={socialFilter.instagram}
                            onChange={(e) =>
                              setSocialFilter((p) => ({
                                ...p,
                                instagram: e.target.checked,
                              }))
                            }
                          />
                          <span>Instagram</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={socialFilter.x}
                            onChange={(e) =>
                              setSocialFilter((p) => ({
                                ...p,
                                x: e.target.checked,
                              }))
                            }
                          />
                          <span>X</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={socialFilter.linkedin}
                            onChange={(e) =>
                              setSocialFilter((p) => ({
                                ...p,
                                linkedin: e.target.checked,
                              }))
                            }
                          />
                          <span>LinkedIn</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 flex justify-end">
                    <button
                      type="button"
                      className="px-4 py-1.5 rounded-full bg-black text-white text-xs font-semibold hover:bg-slate-900"
                      onClick={() => setFiltersOpen(false)}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* View mode pill */}
            <button className="inline-flex items-center px-4 py-2 rounded-full bg-white text-xs font-medium text-slate-700 border border-slate-200 shadow-sm">
              Month
            </button>
          </div>
        </div>

        {/* Weekdays row */}
        <div className="grid grid-cols-7 text-sm text-slate-500 mb-3 px-2">
          <div className="text-center">Mon</div>
          <div className="text-center">Tue</div>
          <div className="text-center">Wed</div>
          <div className="text-center">Thu</div>
          <div className="text-center">Fri</div>
          <div className="text-center">Sat</div>
          <div className="text-center">Sun</div>
        </div>

        {/* Calendar grid in big white card */}
        <div className="bg-white rounded-3xl shadow-sm p-4">
          <div ref={gridRef} className="grid grid-cols-7 gap-4 min-h-[520px]" />
        </div>
      </main>

      {/* Bulk toolbar */}
      {selectedCount > 0 && (
        <div className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 p-3 flex justify-center">
          <div className="flex items-center gap-3">
            <div className="text-sm text-slate-600">{selectedCount} selected</div>
            <button
              onClick={deleteSelected}
              className="px-4 py-2 rounded-lg bg-red-600 text-white hover:opacity-95"
            >
              Delete selected
            </button>
          </div>
        </div>
      )}

      {previewPost && (
        <PlannerPostModal
          open={!!previewPost}
          post={previewPost}
          onClose={() => setPreviewPost(null)}
          providers={[]} // or your real providers list later
        />
      )}
    </div>
  );
}
